import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import {
    Box, Button, Container, SpaceBetween, StatusIndicator, Tabs, SegmentedControl, Input
} from "@amzn/awsui-components-react";
import { AgGridReact } from 'ag-grid-react';
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import './styles.scss';
import tieoutLogo from '../../images/tieoutLogo.png';
import { getDocumentTreeWithTracks, getTickNTieSections, batchGetTickNTieStatus, listAllServicesForApp, getServiceReferences, getServiceReferencesArray, getDocumentFromStore } from "./DocumentStoreAPIs";
import ViewSupportModal from './ViewSupportModal';
import { createPreference, deletePreference, listPreference, listServicesWithTracksForQuarter } from "src/utilities/AppSyncPlusAPIs";
import { deepEqual } from "src/Ateam-BI/utils/generalUtils";
import { v4 as uuidv4 } from 'uuid';
import TieOutResultContent from './TieOutResultContent';
import TieOutHistoryComponent from './TieOutHistoryComponent';
import { convertToLocalTime } from './Utility';


const TieOutPage = ({ quarter, associationId, associationType }) => {
    const tabsRef = useRef();
    const userId = useSelector((globalState) => globalState.auth.userId);
    const currentStage = useSelector((globalState) => globalState.auth.currentStage);

    const [selectedDocument, setSelectedDocument] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const docParam = (params.get('document') || '').toLowerCase();
        if (docParam === '10k' || docParam === '10q') {
            return docParam;
        }
        return '10q';
    });

    const gridRef = useRef();
    const [entityList, setEntityList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [quickFilterText, setQuickFilterText] = useState('');
    const [selectedItems, setSelectedItems] = useState([]);
    const [resultTabs, setResultTabs] = useState([]);
    const [selectedId, setSelectedId] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const versionParam = (params.get('version') || '').toLowerCase();
        if (versionParam === 'pre-wb' || versionParam === 'post-wb') {
            return versionParam;
        }
        return 'pre-wb';
    });
    const [showSupportModal, setShowSupportModal] = useState(false);
    const [savedFilters, setSavedFilters] = useState({});
    const [haveFiltersChanged, setHaveFiltersChanged] = useState(false);
    const [pendingFilterModel, setPendingFilterModel] = useState(null);
    const [dataRendered, setDataRendered] = useState(false);
    const [deliverableOptions, setDeliverableOptions] = useState([]);
    const [deliverablesLoading, setDeliverablesLoading] = useState(false);
    const [tracksData, setTracksData] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [documentOptions, setDocumentOptions] = useState([]);

    const TIEOUT_FILTER_PREFERENCE_TYPE = `TIEOUT_FILTER`;
    const TIEOUT_ASSOCIATION_ID = `TIEOUT_FILTER_ALIAS#${userId}`;
    const TIEOUT_ASSOCIATION_TYPE = 'TIEOUT_FILTER_ALIAS';

    // Cache refs for lazy loading - data is fetched once and reused
    const cache = useRef({
        sections: {},        // keyed by quarter + documentName
        syntheaItems: null,  // fetched once, filtered client-side
        statusMap: {},       // keyed by quarter + wbTypeKey + documentName
        deliverables: null,  // fetched once per associationId
        tracks: null,        // fetched once per associationId + quarter
        _quarter: quarter,
        _associationId: associationId,
    });

    // Reset cache when quarter or associationId changes
    useEffect(() => {
        if (cache.current._quarter !== quarter || cache.current._associationId !== associationId) {
            cache.current = {
                sections: {},
                syntheaItems: null,
                statusMap: {},
                deliverables: null,
                tracks: null,
                _quarter: quarter,
                _associationId: associationId,
            };
        }
    }, [quarter, associationId]);

    // Helper to update URL query params without page reload
    const updateUrlParams = (params) => {
        const url = new URL(window.location);
        Object.entries(params).forEach(([key, value]) => {
            url.searchParams.set(key, value);
        });
        window.history.replaceState({}, '', url);
    };

    // Fetch deliverable options for ViewSupportModal mapping (lazy loaded - cached per associationId)
    const fetchDeliverables = async () => {
        if (!associationId) return;
        if (cache.current.deliverables) {
            setDeliverableOptions(cache.current.deliverables);
            return;
        }
        setDeliverablesLoading(true);
        try {
            const services = await listAllServicesForApp(associationId);
            const options = [];
            (services?.frisco || []).forEach(f => {
                const refs = getServiceReferencesArray(f);
                const refStr = getServiceReferences(f);
                options.push({
                    label: f.label,
                    value: String(f.mapping_id),
                    description: refStr || '',
                    filteringTags: refs,
                    deliverableType: 'FRISCO_MAPPING_ID',
                });
            });
            (services?.flexico || []).forEach(f => {
                const refs = getServiceReferencesArray(f);
                const refStr = getServiceReferences(f);
                options.push({
                    label: f.label,
                    value: String(f.mapping_id),
                    description: refStr || 'Automated',
                    filteringTags: refs,
                    deliverableType: 'FLEXICO_MAPPING_ID',
                });
            });
            cache.current.deliverables = options;
            setDeliverableOptions(options);
        } catch (e) {
            console.error('Failed to fetch deliverables for ViewSupport', e);
        } finally {
            setDeliverablesLoading(false);
        }
    };

    // Fetch tracks data for ViewSupportModal status display (lazy loaded - cached per quarter)
    const fetchTracksData = () => {
        if (!associationId || !quarter) return;
        if (cache.current.tracks) {
            setTracksData(cache.current.tracks);
            return;
        }
        listServicesWithTracksForQuarter(associationId, null, quarter, true,
            (result) => {
                const data = JSON.parse(result.data.listServicesWithTracksForQuarter);
                cache.current.tracks = data || [];
                setTracksData(data || []);
            },
            () => setTracksData([])
        );
    };

    // Fetch document type options from the store (same source as SectionManagement)
    const fetchDocumentOptions = async () => {
        if (!associationId) return;
        try {
            const docs = await getDocumentFromStore({ associationId, associationType });
            const options = (docs || []).map(d => ({
                text: d.document_name,
                id: d.document_name.toLowerCase(),
            }));
            if (options.length > 0) {
                setDocumentOptions(options);
                // If the currently selected document isn't in the options, select the first one
                const currentExists = options.some(o => o.id === selectedDocument);
                if (!currentExists) {
                    setSelectedDocument(options[0].id);
                    fetchTieOutData(selectedId, options[0].id);
                }
            }
        } catch (e) {
            console.error('Failed to fetch document options', e);
        }
    };

    // Load deliverables, tracks, and document options when component mounts
    useEffect(() => {
        if (associationId && quarter) {
            fetchDeliverables();
            fetchTracksData();
            fetchDocumentOptions();
        }
    }, [associationId, quarter]);

    useEffect(() => {
        if (currentStage) {
            fetchTieOutData(selectedId);
        }
    }, [currentStage]);

    const fetchTieOutData = async (wbType, docType, forceRefresh = false) => {
        setIsLoading(true);
        const documentFilter = docType || selectedDocument;
        try {
            const version = wbType === 'pre-wb' ? 'Pre-WB' : 'Post-WB';
            const wbTypeKey = wbType === 'pre-wb' ? 'PRE_WB' : 'POST_WB';
            const documentName = documentFilter.toUpperCase();
            const cacheKey = `${wbTypeKey}_${documentName}`;

            // Step 1: Get all sections (lazy loaded - cached by documentName)
            let sectionsToDisplay;
            if (!forceRefresh && cache.current.sections[documentName]) {
                sectionsToDisplay = cache.current.sections[documentName].map(s => ({ ...s, version }));
            } else {
                const docTreeData = await getDocumentTreeWithTracks({ quarter, associationId, associationType, documentName });
                sectionsToDisplay = (Array.isArray(docTreeData) ? docTreeData : []).map(row => {
                    const attrs = typeof row.custom_attributes === 'string'
                        ? JSON.parse(row.custom_attributes)
                        : (row.custom_attributes || {});
                    const preparers = typeof row.preparers === 'string' ? JSON.parse(row.preparers) : (row.preparers || []);
                    const reviewers = typeof row.reviewers === 'string' ? JSON.parse(row.reviewers) : (row.reviewers || []);
                    return {
                        ...row,
                        section: row.title || row.block_name || '',
                        page: attrs.PAGE || '',
                        tieout: attrs.TIE_OUT_YN || '',
                        preparer: preparers.map(p => p.alias).join(', '),
                        preparerName: preparers.map(p => p.name || p.alias).join(', '),
                        reviewer: reviewers.map(r => r.alias).join(', '),
                        reviewerName: reviewers.map(r => r.name || r.alias).join(', '),
                        service_mappings: typeof row.service_mappings === 'string' ? JSON.parse(row.service_mappings) : (row.service_mappings || []),
                        version,
                    };
                });
                cache.current.sections[documentName] = sectionsToDisplay;
            }

            // Step 2: Get tick-n-tie records from synthea table (lazy loaded - fetched once per quarter)
            // Build project identifier from quarter prop (e.g. "Q2 2026" -> "SEC_CENTRAL#Q2#2026")
            const [q, year] = (quarter || '').split(' ');
            const expectedProject = `SEC_CENTRAL#${q}#${year}`;

            let syntheaItems;
            if (!forceRefresh && cache.current.syntheaItems) {
                syntheaItems = cache.current.syntheaItems;
            } else {
                const syntheaSections = await getTickNTieSections(expectedProject);
                syntheaItems = Array.isArray(syntheaSections) ? syntheaSections : [];
                cache.current.syntheaItems = syntheaItems;
            }

            // Filter synthea records by wb_type based on unique_id pattern: latest#{block_id}#{wb_type}
            const filteredSynthea = syntheaItems.filter(item => {
                const uniqueId = item.unique_id || '';
                return uniqueId.startsWith('latest#') && uniqueId.endsWith(`#${wbTypeKey}`);
            });

            // Step 3: Build a map of block_id -> synthea record
            // unique_id format: latest#{block_id}#{wb_type}
            const syntheaByBlockId = {};
            filteredSynthea.forEach(item => {
                const blockId = item.block_id || (item.unique_id || '').split('#')[1] || '';
                syntheaByBlockId[blockId] = item;
            });

            // Step 4: Extract keys for batchGetTickNTieStatus from matched synthea records
            const keys = filteredSynthea
                .filter(item => item.request_id && item.section_id)
                .map(item => ({ request_id: item.request_id, section_id: item.section_id }));

            let statusMap = {};

            if (keys.length > 0) {
                // Step 5: Batch get status (lazy loaded - cached by wbType + document)
                if (!forceRefresh && cache.current.statusMap[cacheKey]) {
                    statusMap = cache.current.statusMap[cacheKey];
                } else {
                    const statusResults = await batchGetTickNTieStatus({ keys });
                    const statusItems = Array.isArray(statusResults) ? statusResults : [];

                    // Build status lookup keyed by request_id
                    statusItems.forEach(statusItem => {
                        if (statusItem && statusItem.request_id) {
                            statusMap[statusItem.request_id] = statusItem;
                        }
                    });
                    cache.current.statusMap[cacheKey] = statusMap;
                }
            }

            // Step 6: Match synthea -> status via request_id, then use block_id from synthea
            // to match back to section management sections via association_id.
            // Flow: section.association_id == synthea.block_id (from unique_id)
            //       synthea.request_id == status.request_id
            // unique_id format: latest#{block_id}#{wb_type}
            const statusByBlockId = {};
            filteredSynthea.forEach(item => {
                const blockId = item.block_id || (item.unique_id || '').split('#')[1] || '';
                const matchedStatus = statusMap[item.request_id] || null;
                statusByBlockId[blockId] = {
                    syntheaRecord: item,
                    statusData: matchedStatus,
                };
            });

            // Step 7: Merge section management sections with tie-out status data
            // Status is derived from whether there is an associated synthea entry:
            //   - No synthea entry for this section -> "Not Run"
            //   - Synthea entry exists but no evaluation response -> "In Progress"
            //   - Evaluation response has a status -> use that status
            const rows = sectionsToDisplay.map(section => {
                const blockId = section.association_id || '';
                const match = statusByBlockId[blockId];
                const syntheaRecord = match?.syntheaRecord;
                const statusData = match?.statusData || {};

                let status = wbType === 'pre-wb' ? section.pre_wb_status : section.post_wb_status;
                if (!syntheaRecord) {
                    // No associated synthea entry means tie-out was never triggered
                    status = 'Not Run';
                }

                // Extract timestamps from status data
                const workivaExportTimestamp = statusData.workiva_export_timestamp || statusData.source_timestamp || '';
                const executionCompletion = statusData.analysis_completed_at || statusData.execution_completion || statusData.completed_at || '';
                const lastUpdated = statusData.last_updated || statusData.updated_at || '';

                return {
                    ...section,
                    ...statusData,
                    section: section.section,
                    version,
                    blockId,
                    preparer: section.preparer,
                    preparerName: section.preparerName,
                    reviewer: section.reviewer,
                    reviewerName: section.reviewerName,
                    workivaExportTimestamp,
                    executionCompletion,
                    status,
                    lastUpdated,
                    request_id: syntheaRecord?.request_id || '',
                    section_id: syntheaRecord?.section_id || '',
                };
            });
            setEntityList(rows);
        } catch (e) {
            console.error('Failed to fetch tie-out data', e);
            setEntityList([]);
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusIndicator = (status) => {
        switch (status) {
            case 'Ready to Print':
                return <StatusIndicator type="success">Ready to Print</StatusIndicator>;
            case 'Reviewed':
                return <StatusIndicator type="success">Reviewed</StatusIndicator>;
            case 'Tied Out':
                return <StatusIndicator type="success">Tied Out</StatusIndicator>;
            case 'Not Run':
                return <StatusIndicator type="stopped">Not Run</StatusIndicator>;
            case 'In Progress':
                return <StatusIndicator type="in-progress">In Progress</StatusIndicator>;
            case 'Pending Review':
                return <StatusIndicator type="pending">Pending Review</StatusIndicator>;
            case 'Error':
                return <StatusIndicator type="error">Error</StatusIndicator>;
            default:
                return <StatusIndicator type="stopped">{status || 'Not Run'}</StatusIndicator>;
        }
    };

    // AG Grid column definitions
    const columnDefs = useMemo(() => [
        {
            headerName: '',
            field: 'checkbox',
            width: 50,
            maxWidth: 50,
            checkboxSelection: true,
            headerCheckboxSelection: true,
            showDisabledCheckboxes: true,
            filter: false,
            sortable: false,
            resizable: false,
            suppressMenu: true,
            lockPosition: true,
        },
        { headerName: 'Section', field: 'section', minWidth: 250, flex: 2 },
        { headerName: 'Version', field: 'version', width: 100 },
        { headerName: 'Preparer', field: 'preparerName', minWidth: 150 },
        { headerName: 'Reviewer', field: 'reviewerName', minWidth: 150 },
        { headerName: 'Workiva Export (Local Time)', field: 'workivaExportTimestamp', minWidth: 220, valueFormatter: (params) => params.value ? convertToLocalTime(params.value) : '' },
        { headerName: 'Execution Completion (Local Time)', field: 'executionCompletion', minWidth: 240, valueFormatter: (params) => params.value ? convertToLocalTime(params.value) : '' },
        { headerName: 'Status', field: 'status', minWidth: 150, cellRenderer: (params) => getStatusIndicator(params.value) },
    ], []);

    const defaultColDef = useMemo(() => ({
        filter: 'agSetColumnFilter',
        sortable: true,
        resizable: true,
        filterParams: { buttons: ['reset', 'apply'], debounceMs: 200 },
    }), []);

    // Handle AG Grid selection changes
    const onSelectionChanged = useCallback(() => {
        const selectedRows = gridRef.current?.api?.getSelectedRows() || [];
        setSelectedItems(selectedRows);
    }, []);

    // Disable selection for "Not Run" rows
    const isRowSelectable = useCallback((node) => {
        return node.data?.status !== 'Not Run';
    }, []);

    // Load saved filters on mount
    useEffect(() => {
        if (userId) {
            loadSavedFilters();
        }
    }, [userId]);

    // Apply pending filter model once data is rendered
    useEffect(() => {
        if (dataRendered && pendingFilterModel) {
            gridRef.current?.api?.setFilterModel(pendingFilterModel);
            setPendingFilterModel(null);
        }
    }, [dataRendered, pendingFilterModel]);

    const loadSavedFilters = () => {
        if (!userId) return;
        listPreference(
            TIEOUT_ASSOCIATION_ID,
            TIEOUT_ASSOCIATION_TYPE,
            TIEOUT_FILTER_PREFERENCE_TYPE,
            function onSuccess(response) {
                const userFilter = response?.data?.listPreference?.items?.[0];
                if (userFilter) {
                    setSavedFilters(userFilter);
                    setHaveFiltersChanged(false);
                    const filterModel = JSON.parse(userFilter.information);
                    setPendingFilterModel(filterModel);
                }
            },
            function onFailure(error) {
                console.error('Error loading saved filters:', error);
            }
        );
    };

    const saveCurrentFilters = async () => {
        if (!gridRef?.current?.api) return;
        const previouslySavedFilters = savedFilters;
        const currentFilterModel = gridRef.current.api.getFilterModel();

        await new Promise((resolve) => {
            const newPreferenceId = uuidv4();
            createPreference(
                newPreferenceId,
                TIEOUT_FILTER_PREFERENCE_TYPE,
                `${userId}'s TieOut Filters`,
                TIEOUT_ASSOCIATION_ID,
                TIEOUT_ASSOCIATION_TYPE,
                userId,
                currentFilterModel,
                null,
                function onSuccess() {
                    const newFilter = {
                        preferenceId: newPreferenceId,
                        preferenceType: TIEOUT_FILTER_PREFERENCE_TYPE,
                        label: `${userId}'s TieOut Filters`,
                        information: JSON.stringify(currentFilterModel),
                    };
                    setSavedFilters(newFilter);
                    setHaveFiltersChanged(false);
                    resolve();
                },
                function onFailure(error) {
                    console.error('Error saving filters:', error);
                    resolve();
                }
            );
        });

        // Delete old filter preference
        if (previouslySavedFilters?.preferenceId) {
            await new Promise((resolve) => {
                deletePreference(
                    previouslySavedFilters.preferenceId,
                    null,
                    () => resolve(),
                    (error) => {
                        console.error('Error deleting old filter:', error);
                        resolve();
                    }
                );
            });
        }
    };

    const onFilterChanged = useCallback(() => {
        if (!gridRef?.current?.api) return;
        const filterModel = gridRef.current.api.getFilterModel();
        const savedFiltersModel = savedFilters?.information ? JSON.parse(savedFilters?.information) : {};
        setHaveFiltersChanged(!deepEqual(savedFiltersModel, filterModel));
    }, [savedFilters]);

    const onFirstDataRendered = useCallback(() => {
        setDataRendered(true);
    }, []);

    const viewResults = (items) => {
        let tabs = [];
        for (let selectedRow of items) {
            tabs.push({
                label: selectedRow.section || 'Result',
                id: selectedRow.section || selectedRow.section_id,
                dismissible: true,
                dismissLabel: "Dismiss",
                onDismiss: () => {
                    setResultTabs(prevTabs => prevTabs.filter(tab => tab.id !== (selectedRow.section || selectedRow.section_id)));
                },
                content: (
                    <TieOutResultContent data={selectedRow} />
                )
            });
        }
        setResultTabs(tabs);
        setTimeout(() => {
            tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    if (showHistory) {
        return <TieOutHistoryComponent quarter={quarter} associationId={associationId} associationType={associationType} onBack={() => setShowHistory(false)} />;
    }

    return (
        <SpaceBetween direction="vertical" size="l">
            <div style={{ display: 'flex', gap: '16px', overflow: 'hidden', maxWidth: '100%' }}>
                {/* Left Panel - AI Assistant Info */}
                <div style={{ flexShrink: 0, width: '280px' }}>
                <Container>
                    <div>
                        <SpaceBetween direction="vertical" size="m">
                            <div style={{ textAlign: 'center' }}>
                                <img
                                    src={tieoutLogo}
                                    alt="AI Assistant"
                                    style={{ maxWidth: '240px', height: 'auto' }}
                                />
                            </div>
                            <Box>
                                <div style={{ lineHeight: '1.6' }}>
                                    <p style={{ marginBottom: '12px' }}>
                                        <strong>Hello, {userId}!</strong>
                                    </p>
                                    <p style={{ marginBottom: '12px' }}>
                                        I am your friendly AI financial statement reviewer. I catch potential issues before Reviewer1(R1) review
                                    </p>
                                    <p style={{ marginBottom: '12px' }}>
                                        The source of the Financial statements is the Workiva bulk export to S3. Results will be available within 2-3 mins from triggering the bulk export.
                                    </p>
                                    <p style={{ marginBottom: '12px' }}>
                                        <strong>Let's get started:</strong>
                                    </p>
                                    <ul style={{ paddingLeft: '20px', marginBottom: '12px' }}>
                                        <li>Select one or multiple entities</li>
                                        <li>Click view results</li>
                                    </ul>
                                    <p>View past reviews in History</p>
                                </div>
                            </Box>
                        </SpaceBetween>
                    </div>
                </Container>
                </div>

                {/* Right Panel - Controls and Table */}
                <div style={{ flex: '1 1 0%', minWidth: 0, overflow: 'hidden' }}>
                    <SpaceBetween direction="vertical" size="s">
                        {/* Buttons Row */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                            <SpaceBetween direction="horizontal" size="m">
                                <Button
                                    onClick={() => viewResults(selectedItems)}
                                    disabled={!selectedItems || selectedItems.length <= 0}
                                    variant="primary"
                                >
                                    View Results
                                </Button>
                                <Button
                                    onClick={() => setShowSupportModal(true)}
                                    disabled={!selectedItems || selectedItems.length <= 0}
                                    variant="normal"
                                >
                                    View Support
                                </Button>
                                <Button
                                    variant="normal"
                                    onClick={() => {
                                        if (!haveFiltersChanged) {
                                            gridRef?.current?.api?.setFilterModel(null);
                                            setQuickFilterText('');
                                        }
                                        saveCurrentFilters();
                                    }}
                                >
                                    {haveFiltersChanged ? 'Save filters' : 'Clear filters'}
                                </Button>
                                <Button
                                    onClick={() => setShowHistory(true)}
                                    variant="normal"
                                >
                                    History
                                </Button>
                            </SpaceBetween>
                        </div>

                        {/* Segmented Controls Row */}
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                            <SegmentedControl
                                selectedId={selectedDocument}
                                onChange={({ detail }) => {
                                    setSelectedDocument(detail.selectedId);
                                    updateUrlParams({ document: detail.selectedId.toUpperCase() });
                                    fetchTieOutData(selectedId, detail.selectedId);
                                }}
                                options={documentOptions.length > 0 ? documentOptions : [
                                    { text: "10K", id: "10k" },
                                    { text: "10Q", id: "10q" },
                                    { text: "8K", id: "8k" },
                                ]}
                            />
                            <SegmentedControl
                                selectedId={selectedId}
                                onChange={({ detail }) => {
                                    setSelectedId(detail.selectedId);
                                    // Update URL param with version value
                                    updateUrlParams({ version: detail.selectedId });
                                    fetchTieOutData(detail.selectedId);
                                }}
                                options={[
                                    { text: "Pre-WB", id: "pre-wb" },
                                    { text: "Post-WB", id: "post-wb" },
                                ]}
                            />
                        </div>

                        {/* Quick Filter */}
                        <Input
                            value={quickFilterText}
                            onChange={({ detail }) => setQuickFilterText(detail.value)}
                            placeholder="Search sections..."
                            type="search"
                        />

                        {/* AG Grid Table */}
                        <div className="ag-theme-alpine" style={{ width: '100%' }}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={entityList}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                rowSelection="multiple"
                                suppressRowClickSelection={true}
                                isRowSelectable={isRowSelectable}
                                onSelectionChanged={onSelectionChanged}
                                onFilterChanged={onFilterChanged}
                                onFirstDataRendered={onFirstDataRendered}
                                quickFilterText={quickFilterText}
                                domLayout="autoHeight"
                                pagination={true}
                                paginationPageSize={20}
                                animateRows={true}
                                overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading sections...</span>'
                                overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No tie-out sections to display.</span>'
                                loading={isLoading}
                            />
                        </div>
                    </SpaceBetween>
                </div>
            </div>

            {/* Results Section */}
            {resultTabs?.length > 0 && (
                <>
                    <Box variant="p" padding={{ top: "l" }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ color: 'rgb(9, 114, 211)' }}
                            >
                                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"></path>
                                <path d="M20 3v4"></path>
                                <path d="M22 5h-4"></path>
                                <path d="M4 17v2"></path>
                                <path d="M5 18H3"></path>
                            </svg>
                            <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                                Results Generated by AI
                            </span>
                        </div>
                    </Box>
                    <Container>
                        <div ref={tabsRef}>
                            <Tabs tabs={resultTabs} />
                        </div>
                    </Container>
                </>
            )}
            {/* View Support Modal */}
            <ViewSupportModal
                visible={showSupportModal}
                onDismiss={() => setShowSupportModal(false)}
                selectedSections={selectedItems}
                deliverableOptions={deliverableOptions}
                tracksData={tracksData}
                wbType={selectedId === 'pre-wb' ? 'Pre-WB' : 'Post-WB'}
                loading={deliverablesLoading}
            />
        </SpaceBetween>
    );
};

export default TieOutPage;
